import axios from 'axios'
import {
  FALLBACK_IMAGE,
  buildProductDescription,
  buildProductJsonLd,
  buildBreadcrumbJsonLd
} from '~/utils/seo/jsonLd'

/**
 * 상품상세 페이지 seoMeta 핸들러
 * - 기존 seoMeta.js의 상품상세 로직을 분리
 * - buildProductDescription()으로 자연어 description 생성
 */
export default async function handleProduct ({ store, route }) {
  const { pdNo } = route.query
  if (!pdNo) { return }

  const canonicalUrl = `https://www.daisomall.co.kr/pd/pdr/SCR_PDR_0001?pdNo=${pdNo}`

  try {
    const [{ data: res }, descResult] = await Promise.all([
      axios.post(process.env.API_BASE_URL + '/pd/pdr/pdDtl/selPdDtlInfo', { pdNo }),
      axios.post(process.env.API_BASE_URL + '/pd/pdr/pdDtl/selPdDtlDesc', { pdNo }).catch(() => null)
    ])

    if (!res.success || !res.data) {
      store.commit('seoMeta/set', {
        title: '다이소몰',
        description: '온라인 국민가게, 다이소',
        ogUrl: canonicalUrl,
        ogImage: FALLBACK_IMAGE,
        canonical: canonicalUrl
      })
      return
    }

    const pd = res.data
    const pdName = pd.exhPdNm || ''
    const brand = pd.brndNm || '다이소'
    const ctgr = pd.exhCtgr && pd.exhCtgr.length > 0 ? pd.exhCtgr[0] : null
    const categoryName = ctgr ? (ctgr.sctgrNm || ctgr.mctgrNm || ctgr.lctgrNm || '') : ''
    const price = pd.pdPrc || ''

    // 상세설명 텍스트 추출
    let detailText = ''
    const descRes = descResult && descResult.data
    if (descRes && descRes.success && descRes.data && descRes.data.pdDtlDesc && descRes.data.pdDtlDesc.vsipPdDtl) {
      detailText = descRes.data.pdDtlDesc.vsipPdDtl
        .replace(/<[^>]+>/g, '')
        .replace(/&[^;]+;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    }

    // 재고 상태
    const availability = (pd.sleStsCd === '03' && pd.exhYn === 'Y' && pd.onldOpt && pd.onldOpt.some(opt => opt.stckQy > 0))
      ? 'InStock'
      : 'OutOfStock'

    // 자연어 description 생성
    const description = buildProductDescription({
      name: pdName,
      brand,
      category: categoryName,
      price,
      detailText,
      rating: pd.revwCnt > 0 ? { avg: pd.revwAvg, count: pd.revwCnt } : null,
      availability
    })

    // 타이틀용 간략 설명
    const shortDesc = [pdName, brand, categoryName, price ? `${Number(price).toLocaleString()}원` : ''].filter(Boolean).join(' | ')

    // 이미지 URL
    const imageUrl = pd.imgUrl
      ? (pd.imgUrl.startsWith('http') ? pd.imgUrl : `https://cdn.daisomall.co.kr${pd.imgUrl}`)
      : FALLBACK_IMAGE

    // Product JSON-LD
    const product = buildProductJsonLd({
      name: pdName,
      image: imageUrl,
      description,
      sku: pdNo,
      brand,
      url: canonicalUrl,
      price,
      availability,
      category: categoryName,
      rating: pd.revwCnt > 0 ? { avg: pd.revwAvg, count: pd.revwCnt } : null
    })

    // BreadcrumbList JSON-LD
    const breadcrumbItems = [{ name: '홈', url: 'https://www.daisomall.co.kr' }]
    if (ctgr) {
      if (ctgr.lctgrNm) { breadcrumbItems.push({ name: ctgr.lctgrNm, url: `https://www.daisomall.co.kr/ds/exbt/${ctgr.lctgrNo || ''}` }) }
      if (ctgr.mctgrNm) { breadcrumbItems.push({ name: ctgr.mctgrNm, url: `https://www.daisomall.co.kr/ds/exbt/${ctgr.mctgrNo || ''}` }) }
      if (ctgr.sctgrNm) { breadcrumbItems.push({ name: ctgr.sctgrNm, url: `https://www.daisomall.co.kr/ds/exbt/${ctgr.sctgrNo || ''}` }) }
    }
    breadcrumbItems.push({ name: pdName, url: canonicalUrl })
    const breadcrumb = buildBreadcrumbJsonLd(breadcrumbItems)

    store.commit('seoMeta/set', {
      title: shortDesc ? `${shortDesc} - 다이소몰` : '다이소몰',
      description,
      ogUrl: canonicalUrl,
      ogImage: imageUrl,
      ogType: 'product',
      canonical: canonicalUrl,
      mobileUrl: `https://m.daisomall.co.kr/pd/pdl/SCR_PDL_0001?pdNo=${pdNo}`,
      h1: pdName,
      jsonLd: [product, breadcrumb]
    })
  } catch (e) {
    console.error('[seoMeta/product]', e.message)
    store.commit('seoMeta/set', {
      title: '다이소몰',
      description: '온라인 국민가게, 다이소',
      ogUrl: canonicalUrl,
      ogImage: FALLBACK_IMAGE,
      canonical: canonicalUrl
    })
  }
}
